import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {FirebaseMessageListItem, MessageMode, ObjectState} from 'types'
import {showModalLimitedAccess, useDebounce} from 'utils'
import {ButtonIcon, Icon, Input, List} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import GiftShopMessageListItem from './GiftShopMessageListItem'
import GiftShopMessageRoomEmpty from './GiftShopMessageRoomEmpty'

const StyledSearchBar = styled.div`
  padding-left: ${convertUnit(20)};
  padding-right: ${convertUnit(20)};
  padding-top: ${convertUnit(16)};
  padding-bottom: ${convertUnit(16)};
`

const StyledIcon = styled(ButtonIcon)`
  cursor: pointer;
  width: ${convertUnit(44)};
  height: ${convertUnit(44)};
  border-radius: ${convertUnit(50)};
  position: absolute;
  bottom: ${convertUnit(20)};
  right: ${convertUnit(35)};
`

export interface GiftShopMessageListHomeProps {
  stateMode: ObjectState<MessageMode>
  stateSelectedMessagesForkygram: ObjectState<string[]>
  stateSelectedMessagesGS: ObjectState<string[]>
  data: FirebaseMessageListItem[]
  initialize: boolean
}

export default function GiftShopMessageList({
  stateMode,
  stateSelectedMessagesForkygram,
  stateSelectedMessagesGS,
  data,
  initialize,
}: GiftShopMessageListHomeProps) {
  const {translate} = useTranslation()
  const [mode, setMode] = stateMode
  const {status: childStatus} = useSelector('parentalConsentState')
  const [search, setSearch] = useState('')
  const debounce = useDebounce()

  const handleKeyExtractor = useCallback(
    (item: FirebaseMessageListItem) => item.id,
    [],
  )

  const handleRenderItem = useCallback(
    (item: FirebaseMessageListItem) => (
      <GiftShopMessageListItem
        data={item}
        search={search}
        stateSelectedMessagesForkygram={stateSelectedMessagesForkygram}
        stateSelectedMessagesGS={stateSelectedMessagesGS}
        mode={mode}
      />
    ),
    [mode, search, stateSelectedMessagesForkygram, stateSelectedMessagesGS],
  )

  const handleRenderEmpty = useMemo(
    () => (initialize ? <GiftShopMessageRoomEmpty /> : undefined),
    [initialize],
  )

  const handleRenderHeader = useMemo(
    () => (
      <StyledSearchBar>
        <Input
          placeholder={translate('global:search')}
          leftIcon={<Icon type="search" color="gray_3" />}
          onChangeText={(text) => debounce(() => setSearch(text))}
        />
      </StyledSearchBar>
    ),
    [debounce, translate],
  )

  return (
    <>
      <List
        listHeaderElement={handleRenderHeader}
        data={
          childStatus ? data.filter((item) => item.room_type !== 'chat') : data
        }
        keyExtractor={handleKeyExtractor}
        renderItem={handleRenderItem}
        listEmptyElement={handleRenderEmpty}
      />
      {mode === 'message' ? (
        <StyledIcon
          iconType="pencil"
          iconColor="white_1"
          onClick={() =>
            childStatus ? showModalLimitedAccess() : setMode('new-message')
          }
        />
      ) : null}
    </>
  )
}
